import axios, { isAxiosError } from "axios";
import i18next from "i18next";
import i18n from "i18next";
import { PropsWithChildren, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import envs from "@/envs";
import PATHS from "@/routes/paths";
import useUserStore from "@/features/auth/store/useUserStore.ts";

const headers = {
  "X-Requested-With": "XMLHttpRequest",
  "Content-type": "application/json",
  accept: "application/json",
  "Accept-Language": i18n.language,
  sameSite: "none",
  secure: "HttpOnly",
};

const axiosApiInstance = axios.create({
  baseURL: envs.VITE_API_URL,
  headers,
  withCredentials: true,
});

const axiosLoginInstance = axios.create({
  baseURL: envs.VITE_API_URL,
  headers,
  withCredentials: true,
});

const AxiosInterceptorProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { user } = useUserStore();

  useEffect(() => {
    // API INSTANCE INTERCEPTORS
    // Request
    axiosApiInstance.interceptors.request.use((req) => {
      req.headers["Accept-Language"] = i18next.language;

      return req;
    });
    // Response
    axiosApiInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 429) {
          navigate(PATHS.TOO_MANY_REQUESTS);
        }
        if (
          isAxiosError<{ code?: "not_onboarded" | "not_authenticated" | "user_already_onboarded" }>(
            error,
          ) &&
          error.config &&
          error.response?.data.code
        ) {
          const responseCode = error.response.data.code;

          if (
            responseCode === "not_authenticated" &&
            location.pathname !== PATHS.SIGN_UP &&
            !user?.isOnboarded
          ) {
            navigate(PATHS.SIGN_IN);
          }
          if (responseCode === "not_onboarded") {
            navigate(PATHS.SIGN_UP);
          }
          if (responseCode === "user_already_onboarded") {
            navigate(PATHS.REDIRECT);
          }
        }

        return Promise.reject(error);
      },
    );

    // LOGIN INSTANCE INTERCEPTORS
    // Request
    axiosLoginInstance.interceptors.request.use((req) => {
      req.headers["Accept-Language"] = i18next.language;

      return req;
    });
    // Response
    axiosLoginInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          isAxiosError<{ code?: "already_authenticated" }>(error) &&
          error.config &&
          error.response
        ) {
          if (error.response.data.code === "already_authenticated") {
            navigate(PATHS.REDIRECT);
          }

          return Promise.reject(error);
        }
      },
    );
  }, []);

  return children;
};

export { AxiosInterceptorProvider, axiosApiInstance, axiosLoginInstance };
